import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const FLOAT_MENU = ({
  anchorEl,
  anchorPosition,
  anchorReference = "anchorEl",
  open,
  onClose,
  children,
  scrollContainerId = "box-body",
  maxHeight,
  zIndex = 1300, // FIX: Dynamic zIndex with a high default to overcome Modals
}) => {
  const menuRef = useRef(null);
  const [render, setRender] = useState(open);
  const [animateOpen, setAnimateOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // 1. HANDLE SCROLL LOCKING (Body + Modal)
  useEffect(() => {
    const modalContainer = document.getElementById(scrollContainerId);

    if (open) {
      document.body.style.overflow = "hidden";
      if (modalContainer) {
        modalContainer.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = "";
      if (modalContainer) {
        modalContainer.style.overflow = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
      if (modalContainer) {
        modalContainer.style.overflow = "";
      }
    };
  }, [open, scrollContainerId]);

  // 2. Mount/unmount logic with animation
  useEffect(() => {
    if (open) {
      setRender(true);
      setTimeout(() => setAnimateOpen(true), 10);
    } else {
      setAnimateOpen(false);
      const timeout = setTimeout(() => setRender(false), 180);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  // 3. Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        anchorReference === "anchorEl" &&
        anchorEl &&
        anchorEl.contains(event.target)
      ) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorEl, anchorReference]);

  // 4. Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleKeyDown, { capture: true });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open, onClose]);

  // 5. Positioning & ResizeObserver
  useEffect(() => {
    if (!render) return;

    const updatePosition = () => {
      const menu = menuRef.current;
      if (!menu) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      const menuWidth = menu.offsetWidth;
      const menuHeight = menu.offsetHeight;

      let top = 0;
      let left = 0;

      // MODE 1: Pixel Coordinates
      if (anchorReference === "anchorPosition" && anchorPosition) {
        left = anchorPosition.left + scrollX;
        top = anchorPosition.top + scrollY;

        // Flip menu to the left if it spills off the right side
        if (left + menuWidth > scrollX + viewportWidth) {
          left -= menuWidth;
        }
        // Flip menu up if it spills off the bottom
        if (top + menuHeight > scrollY + viewportHeight) {
          top -= menuHeight;
        }
      }
      // MODE 2: HTML Element Binding
      else if (
        anchorReference === "anchorEl" &&
        anchorEl &&
        anchorEl.getBoundingClientRect
      ) {
        const rect = anchorEl.getBoundingClientRect();
        top = rect.bottom + scrollY;
        left = rect.left + scrollX;

        if (left + menuWidth > scrollX + viewportWidth) {
          left = Math.max(scrollX, rect.right - menuWidth + scrollX);
        }

        // Recalculate top coordinate based on actual menuHeight (which triggers on resize)
        if (top + menuHeight > scrollY + viewportHeight) {
          top = Math.max(scrollY, rect.top - menuHeight + scrollY);
        }
      } else {
        return; // No valid anchor provided
      }

      // Failsafe boundaries
      if (left < scrollX) left = scrollX + 10;
      if (top < scrollY) top = scrollY + 10;

      setPosition({ top, left });
    };

    // Run immediately on mount
    updatePosition();

    // Attach ResizeObserver to watch for shrinking/growing content
    const menuElement = menuRef.current;
    let resizeObserver;

    if (menuElement) {
      resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserver.observe(menuElement);
    }

    // Keep it glued if the window resizes
    window.addEventListener("resize", updatePosition);

    // Cleanup observers and listeners
    return () => {
      if (resizeObserver && menuElement) {
        resizeObserver.unobserve(menuElement);
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", updatePosition);
    };
  }, [render, anchorEl, anchorPosition, anchorReference]);

  if (!render) return null;

  const menuElement = (
    <div
      ref={menuRef}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        zIndex: zIndex, // FIX: Assigned dynamic zIndex here
        background: "#fff",
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "13px",
        padding: "13px",
        transformOrigin: "top left",
        transition: "transform 180ms ease-out, opacity 180ms ease-out",
        transform: animateOpen ? "scale(1)" : "scale(0.8)",
        opacity: animateOpen ? 1 : 0,
        pointerEvents: animateOpen ? "auto" : "none",
        maxWidth: "80vw",
        maxHeight: maxHeight, // FIX: Applied the maxHeight prop
        overflowY: maxHeight ? "auto" : "visible", // FIX: Add scroll if maxHeight is defined
      }}
    >
      {children}
    </div>
  );

  return createPortal(menuElement, document.body);
};

export default FLOAT_MENU;
